/* Admin-only visual preview of the two ExamEngine states the operator
   can't easily trigger without wall-time / a full exam attempt:

   - /admin/exam-preview/pass      → pass banner + score card + CTAs
   - /admin/exam-preview/cooldown  → cooldown-blocked screen

   Feb 2026: added so the operator can review the 90-min cooldown copy
   and the new pass-success moment without waiting 90 minutes or
   simulating a full 50-question submission. Gated on admin JWT presence
   (light gate — this route renders no real student data). */
import { useSearchParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LmsShell, getAdminToken } from "./portal/shell";
import { ExamResults } from "./portal/ExamEngine";

// Mock result payload — mirrors ExamSubmitResponse shape so
// <ExamResults /> renders identically to the real thing.
const MOCK_PASS = {
  attempt_id: "preview-mock-attempt",
  score_pct: 91.3,
  passed: true,
  pass_pct: 80,
  total: 50,
  correct: 46,
  submitted_at: new Date().toISOString(),
  next_attempt_at: new Date(Date.now() + 90 * 60 * 1000).toISOString(),
  time_expired: false,
  results: [],  // Empty — the pass banner + score card don't depend on this
};

// Mock cooldown message — matches the tone of what the real
// exam-start endpoint returns when a student hits it under cooldown.
const MOCK_COOLDOWN_MSG =
  "You completed a practice exam 42 minutes ago. Your next attempt is " +
  "available in about 48 minutes.";

export default function AdminExamPreview() {
  const [sp] = useSearchParams();
  const state = sp.get("state") || "pass";

  // Light auth gate — presence of an admin token in localStorage.
  // No real student data is rendered here; the sole risk of leaking
  // is showing operational UI copy, which is already public on the
  // marketing site (/groundschool § 03·B). Full JWT validation isn't
  // worth the ceremony for a mock preview page.
  if (!getAdminToken()) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <LmsShell>
      <div className="tbg-lms-container" style={{ padding: 24 }}>
        <div style={{
          marginBottom: 20,
          padding: "10px 14px",
          background: "rgba(240,190,60,0.08)",
          border: "1px solid rgba(240,190,60,0.4)",
          borderRadius: 4,
          font: "600 10px 'JetBrains Mono', monospace",
          letterSpacing: "0.24em",
          color: "#f0be3c",
          textTransform: "uppercase",
        }} data-testid="admin-preview-banner">
          Admin Preview · Mock data · Not a real attempt
        </div>

        {/* Nav between the two preview states */}
        <div style={{ display: "flex", gap: 10, marginBottom: 22 }}>
          <Link
            to="/admin/exam-preview?state=pass"
            className={"tbg-btn" + (state === "pass" ? "" : " ghost")}
            data-testid="admin-preview-nav-pass">
            Pass success moment
          </Link>
          <Link
            to="/admin/exam-preview?state=cooldown"
            className={"tbg-btn" + (state === "cooldown" ? "" : " ghost")}
            data-testid="admin-preview-nav-cooldown">
            Cooldown-blocked screen
          </Link>
          <Link to="/admin/authoring" className="tbg-btn ghost"
            data-testid="admin-preview-back">
            <ArrowLeft size={12} /> Back to admin
          </Link>
        </div>

        {state === "pass" && <ExamResults result={MOCK_PASS} />}

        {state === "cooldown" && (
          <div className="tbg-panel" data-testid="admin-preview-cooldown">
            <div className="tbg-section-sub">Cooldown</div>
            <h1 className="tbg-section-title">Not yet</h1>
            <p style={{ color: "#a5b8b3", lineHeight: 1.6, marginTop: 8 }}>
              {MOCK_COOLDOWN_MSG}
            </p>
            <p style={{ color: "#a5b8b3", lineHeight: 1.6, marginTop: 8, fontSize: 13 }}>
              Our practice exam enforces a <b>90-minute cooldown</b> between attempts —
              enough time to review the rationale for the ones you missed and
              revisit the modules where you scored below 80%.
            </p>
            <p style={{ color: "#7b8b87", lineHeight: 1.6, marginTop: 8, fontSize: 12 }}>
              Note · The real Transport Canada Small Advanced Exam requires a
              full <b>24-hour</b> rewrite waiting period. This tool is looser by
              design for study flow.
            </p>
            <Link to="/dashboard" className="tbg-btn" style={{ marginTop: 16 }}>
              <ArrowLeft size={13} /> Back to modules
            </Link>
          </div>
        )}
      </div>
    </LmsShell>
  );
}
